import type { EsConditionConfig } from "@/components/ConditionPageLayoutEs";
import {
  BRAIN_IMG,
  ES_REVIEWED_BY,
  ES_LAST_UPDATED,
  esBaseCrumbs,
} from "./esConditionShared";

export const esMigraines: EsConditionConfig = {
  canonical: "/es/migraines-treatment-coral-springs-fl",
  badge: "Terapia con ketamina IV",
  title: "Tratamiento de las migrañas con ketamina",
  subtitle:
    "La infusión de ketamina IV puede ofrecer alivio para las migrañas crónicas resistentes al tratamiento en Coral Springs, FL.",
  introHeading:
    "Alivio de las migrañas con tratamiento de ketamina IV en Coral Springs, FL",
  introText: [
    "Las migrañas crónicas pueden ser incapacitantes, con dolor intenso, náuseas y sensibilidad a la luz y al sonido. Cuando los tratamientos preventivos y de rescate convencionales no ofrecen suficiente alivio, la infusión de ketamina IV puede ayudar.",
    "Ofrecemos tratamiento con ketamina IV como una opción complementaria basada en evidencia para adultos con migrañas crónicas en Coral Springs, FL.",
  ],
  whatIsTitle: "¿Qué es la ketamina para las migrañas?",
  whatIsText: [
    "La ketamina ha mostrado potencial para reducir la frecuencia y la intensidad de las migrañas en algunos pacientes resistentes al tratamiento.",
    "La infusión de ketamina IV en dosis baja actúa sobre el glutamato y los receptores NMDA, que se cree están implicados en la sensibilización cortical que subyace a las migrañas crónicas.",
  ],
  howItHelpsTitle: "¿Cómo ayuda la ketamina con las migrañas?",
  howItHelpsText: [
    "Las migrañas crónicas implican una sensibilización del sistema nervioso central. La ketamina modula la actividad del glutamato y los receptores NMDA, lo que puede ayudar a reducir esta sensibilización y la frecuencia de los episodios.",
    "El tratamiento se coordina como parte de un plan integral que puede incluir medicación preventiva y cambios en el estilo de vida.",
  ],
  treatmentExperienceTitle: "¿Cómo es una sesión de tratamiento con ketamina?",
  treatmentExperienceText: [
    "Cada sesión se realiza en nuestro centro en Coral Springs, FL, con supervisión médica continua. La infusión de dosis baja se administra lentamente en un entorno seguro y controlado.",
    "Nuestro equipo clínico está presente durante toda la sesión para garantizar su seguridad y comodidad.",
  ],
  benefitsTitle: "Beneficios del tratamiento con ketamina para las migrañas",
  benefits: [
    {
      title: "Reducción de la frecuencia",
      desc: "La ketamina puede ayudar a reducir la frecuencia de los episodios de migraña crónica.",
    },
    {
      title: "Se dirige a la sensibilización central",
      desc: "Modula los receptores NMDA implicados en la sensibilización cortical.",
    },
    {
      title: "Enfoque complementario",
      desc: "Se puede combinar con medicación preventiva y otros enfoques.",
    },
    {
      title: "Cuidado ambulatorio",
      desc: "El tratamiento se realiza en consultorio, sin hospitalización.",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Tratamiento de las migrañas en Coral Springs, FL",
  activeConditionHref: "/es/migraines-treatment-coral-springs-fl",
  seoTitle: "Ketamina para las migrañas en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Infusión de ketamina IV para las migrañas crónicas resistentes al tratamiento en Coral Springs, FL. Cuidado complementario basado en evidencia.",
  keywords:
    "ketamina migrañas Coral Springs, tratamiento migrañas Coral Springs FL, migraña crónica ketamina",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs(
    "Migrañas",
    "/es/migraines-treatment-coral-springs-fl",
  ),
};

export const esNeuropathicPain: EsConditionConfig = {
  canonical: "/es/neuropathic-pain-treatment-coral-springs-fl",
  badge: "Terapia con ketamina IV",
  title: "Tratamiento del dolor neuropático con ketamina",
  subtitle:
    "La infusión de ketamina IV puede ofrecer alivio para el dolor neuropático cuando los tratamientos convencionales no son suficientes, en Coral Springs, FL.",
  introHeading:
    "Alivio del dolor neuropático con tratamiento de ketamina IV en Coral Springs, FL",
  introText: [
    "El dolor neuropático, causado por lesiones o disfunción del sistema nervioso, puede ser particularmente difícil de tratar. Cuando los analgésicos convencionales y los medicamentos para el dolor neuropático no ofrecen suficiente alivio, la infusión de ketamina IV puede ayudar.",
    "Ofrecemos tratamiento con ketamina IV como una opción complementaria basada en evidencia para adultos con dolor neuropático en Coral Springs, FL.",
  ],
  whatIsTitle: "¿Qué es la ketamina para el dolor neuropático?",
  whatIsText: [
    "La ketamina ha mostrado eficacia particular para el dolor neuropático al actuar sobre los receptores NMDA implicados en la transmisión y la amplificación del dolor neuropático.",
    "La infusión de ketamina IV en dosis baja puede ayudar a reducir la intensidad del dolor neuropático en algunos pacientes que no han respondido a otros tratamientos.",
  ],
  howItHelpsTitle: "¿Cómo ayuda la ketamina con el dolor neuropático?",
  howItHelpsText: [
    "El dolor neuropático implica una sobreactivación de los receptores NMDA en el sistema nervioso, que amplifica las señales de dolor. La ketamina bloquea estos receptores, lo que puede ayudar a reducir la intensidad del dolor.",
    "A diferencia de los opioides, que conllevan riesgos significativos de dependencia, la ketamina IV ofrece un mecanismo de acción diferente para el manejo del dolor neuropático.",
  ],
  treatmentExperienceTitle: "¿Cómo es una sesión de tratamiento con ketamina?",
  treatmentExperienceText: [
    "Cada sesión se realiza en nuestro centro en Coral Springs, FL, con supervisión médica continua. La infusión de dosis baja se administra lentamente en un entorno seguro y controlado.",
    "Nuestro equipo clínico está presente durante toda la sesión para garantizar su seguridad y comodidad.",
  ],
  benefitsTitle:
    "Beneficios del tratamiento con ketamina para el dolor neuropático",
  benefits: [
    {
      title: "Reducción del dolor",
      desc: "La ketamina puede ayudar a reducir la intensidad del dolor neuropático.",
    },
    {
      title: "Se dirige a los receptores NMDA",
      desc: "Modula los receptores implicados en la amplificación del dolor neuropático.",
    },
    {
      title: "Alternativa a los opioides",
      desc: "Ofrece un mecanismo de acción diferente con un perfil de efectos secundarios distinto.",
    },
    {
      title: "Cuidado ambulatorio",
      desc: "El tratamiento se realiza en consultorio, sin hospitalización.",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Tratamiento del dolor neuropático en Coral Springs, FL",
  activeConditionHref: "/es/neuropathic-pain-treatment-coral-springs-fl",
  seoTitle:
    "Ketamina para el dolor neuropático en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Infusión de ketamina IV para el dolor neuropático en Coral Springs, FL. Una alternativa basada en evidencia cuando los tratamientos convencionales no son suficientes.",
  keywords:
    "ketamina dolor neuropático Coral Springs, tratamiento dolor neuropático Coral Springs FL, dolor nervioso ketamina",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs(
    "Dolor neuropático",
    "/es/neuropathic-pain-treatment-coral-springs-fl",
  ),
};

export const esKetamineOverview: EsConditionConfig = {
  canonical: "/es/ketamine-treatment-coral-springs-fl",
  badge: "Terapia con ketamina IV",
  title: "Tratamiento con ketamina en Coral Springs, FL",
  subtitle:
    "Opciones avanzadas de tratamiento con ketamina IV para la depresión resistente al tratamiento, la ansiedad, el TEPT y el dolor crónico en Neuro Wellness TMS Centers of America.",
  introHeading: "Terapia de infusión de ketamina IV en Coral Springs, FL",
  introText: [
    "La ketamina IV es un tratamiento basado en evidencia que ha mostrado eficacia para una serie de afecciones resistentes al tratamiento, incluida la depresión, la ansiedad, el TEPT y ciertos tipos de dolor crónico.",
    "Ofrecemos infusión de ketamina IV como una opción complementaria para adultos en Coral Springs, FL, siempre coordinada con su plan integral de cuidado.",
  ],
  whatIsTitle: "¿Qué es la terapia de infusión de ketamina IV?",
  whatIsText: [
    "La ketamina, utilizada durante mucho tiempo como anestésico, ha mostrado en investigaciones recientes un potencial para el tratamiento de varias afecciones de salud mental y dolor crónico resistentes a otros enfoques.",
    "La infusión de ketamina IV en dosis baja actúa sobre el glutamato y los receptores NMDA del cerebro, que desempeñan un papel clave en la regulación del estado de ánimo y la percepción del dolor.",
  ],
  howItHelpsTitle: "¿Cómo funciona la ketamina IV?",
  howItHelpsText: [
    "A diferencia de los antidepresivos orales, que pueden tardar semanas en hacer efecto, la ketamina puede ofrecer un alivio notable en horas o días al modular el glutamato, un neurotransmisor clave del cerebro.",
    "Cada plan de tratamiento se personaliza según su diagnóstico, su historial de tratamiento y sus objetivos, y se coordina con su psiquiatra como parte de un enfoque integral.",
  ],
  treatmentExperienceTitle: "¿Cómo es una sesión de tratamiento con ketamina?",
  treatmentExperienceText: [
    "Cada sesión se realiza en nuestro centro en Coral Springs, FL, con supervisión médica continua. La infusión de dosis baja se administra lentamente, generalmente durante 40 a 60 minutos, en un entorno seguro y controlado.",
    "Nuestro equipo clínico está presente durante toda la sesión para garantizar su seguridad y comodidad.",
  ],
  benefitsTitle: "Afecciones tratadas con ketamina IV",
  benefits: [
    {
      title: "Depresión resistente al tratamiento",
      desc: "Alivio potencialmente rápido cuando los antidepresivos orales no son suficientes.",
    },
    {
      title: "Trastornos de ansiedad",
      desc: "Reducción de los síntomas de ansiedad crónica y ataques de pánico.",
    },
    {
      title: "TEPT y trauma",
      desc: "Modulación de los circuitos cerebrales del miedo y el trauma.",
    },
    {
      title: "Dolor crónico y neuropático",
      desc: "Una alternativa a los opioides para ciertos tipos de dolor crónico.",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Tratamiento con ketamina en Coral Springs, FL",
  activeConditionHref: "/es/ketamine-treatment-coral-springs-fl",
  seoTitle: "Tratamiento con ketamina en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Terapia de infusión de ketamina IV para la depresión resistente al tratamiento, la ansiedad, el TEPT y el dolor crónico en Coral Springs, FL. Cuidado basado en evidencia.",
  keywords:
    "tratamiento con ketamina Coral Springs, ketamina IV Coral Springs FL, terapia de infusión de ketamina",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs(
    "Ketamina",
    "/es/ketamine-treatment-coral-springs-fl",
  ),
};

export const esKap: EsConditionConfig = {
  canonical: "/es/ketamine-assisted-psychotherapy-coral-springs-fl",
  badge: "Psicoterapia asistida con ketamina",
  title: "Psicoterapia asistida con ketamina en Coral Springs, FL",
  subtitle:
    "La integración de la infusión de ketamina IV con la psicoterapia para un trabajo emocional más profundo en Neuro Wellness TMS Centers of America.",
  introHeading: "Psicoterapia asistida con ketamina (KAP) en Coral Springs, FL",
  introText: [
    "La psicoterapia asistida con ketamina (KAP, por sus siglas en inglés) combina la infusión de ketamina IV con sesiones de psicoterapia guiada, aprovechando el estado expandido de conciencia que induce la ketamina para facilitar un trabajo emocional más profundo.",
    "Ofrecemos KAP como una opción integrada para adultos en Coral Springs, FL, coordinada por nuestros clínicos autorizados.",
  ],
  whatIsTitle: "¿Qué es la psicoterapia asistida con ketamina?",
  whatIsText: [
    "La KAP combina la infusión de ketamina IV con la psicoterapia guiada. La ketamina induce un estado expandido de conciencia que puede facilitar el acceso a pensamientos, emociones y perspectivas que pueden ser difíciles de alcanzar en la terapia tradicional.",
    "Las sesiones de psicoterapia antes y después de la infusión ayudan a integrar las experiencias y traducirlas en cambios duraderos.",
  ],
  howItHelpsTitle: "¿Cómo ayuda la KAP?",
  howItHelpsText: [
    "Al combinar los efectos neuroquímicos de la ketamina con el marco estructurado de la psicoterapia, la KAP puede ayudar a los pacientes a procesar el trauma, explorar patrones de pensamiento arraigados y desarrollar nuevas perspectivas.",
    "El enfoque integrado puede ser particularmente útil para afecciones resistentes al tratamiento, donde la medicación o la psicoterapia por sí solas no han sido suficientes.",
  ],
  treatmentExperienceTitle: "¿Cómo es una sesión de KAP?",
  treatmentExperienceText: [
    "Una sesión típica de KAP incluye una preparación con su terapeuta, la infusión de ketamina IV en un entorno seguro y supervisado, y una sesión de integración posterior para procesar la experiencia.",
    "Nuestro equipo clínico está presente durante toda la sesión para garantizar su seguridad y apoyar el trabajo terapéutico.",
  ],
  benefitsTitle: "Beneficios de la psicoterapia asistida con ketamina",
  benefits: [
    {
      title: "Trabajo emocional más profundo",
      desc: "El estado expandido de conciencia puede facilitar el acceso a material emocional difícil.",
    },
    {
      title: "Enfoque integrado",
      desc: "Combina la infusión de ketamina con la psicoterapia guiada para una integración duradera.",
    },
    {
      title: "Para afecciones resistentes al tratamiento",
      desc: "Puede ayudar cuando la medicación o la psicoterapia por sí solas no han sido suficientes.",
    },
    {
      title: "Cuidado ambulatorio",
      desc: "El tratamiento se realiza en consultorio, sin hospitalización.",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Psicoterapia asistida con ketamina en Coral Springs, FL",
  activeConditionHref: "/es/ketamine-assisted-psychotherapy-coral-springs-fl",
  seoTitle:
    "Psicoterapia asistida con ketamina en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Psicoterapia asistida con ketamina (KAP) en Coral Springs, FL. Integración de la infusión de ketamina IV con la psicoterapia guiada para un trabajo emocional más profundo.",
  keywords:
    "psicoterapia asistida con ketamina Coral Springs, KAP Coral Springs FL, ketamina con psicoterapia",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs(
    "Psicoterapia asistida con ketamina",
    "/es/ketamine-assisted-psychotherapy-coral-springs-fl",
  ),
};
